import type { AIChatSession, AIChatSessionMessage } from '@/types/generated/types.gen'

const MAX_PREVIEW_LENGTH = 80

// Structured-content chunk shape. The generated `AIChatSessionMessage.content` types as
// `string | null`, but assistant messages serialize as `{ thinking, chunks, ... }` at
// runtime (see `app/ai/types/chat_session_types.py::AssistantMessageChunks`). Until that
// is reflected in the generated types, narrow locally rather than via `any`.
type TextChunk = { type: 'text'; text?: string; content?: string }
type AnyChunk = { type?: string }

/**
 * Flatten a chat session message into a single line of text.
 *
 * Handles both content shapes the backend can produce:
 *
 * - Plain string content (user / tool messages).
 * - Structured ``{ chunks: [...] }`` content (assistant messages, where chunks
 *   alternate between text and entity / document / verdict references). Only
 *   the ``text``-type chunks contribute to the preview — non-text chunks carry
 *   semantic anchors that don't read meaningfully in a one-line summary.
 *
 * The ``c.text ?? c.content`` chain tolerates an obsolete chunk shape that may
 * still exist on legacy rows; safe no-op for current chunks.
 */
export function extractMessageText(message: AIChatSessionMessage): string {
  const content = message.content
  if (typeof content === 'string') return content
  if (content && typeof content === 'object' && 'chunks' in content) {
    const chunks = (content as { chunks: AnyChunk[] }).chunks
    if (Array.isArray(chunks)) {
      return chunks
        .filter((c): c is TextChunk => c?.type === 'text')
        .map((c) => c.text ?? c.content ?? '')
        .join(' ')
    }
  }
  return ''
}

/**
 * Best-effort display title for a chat session.
 *
 * Returns the persisted ``title`` when set, otherwise a trimmed preview of the
 * first message so titleless chats with content remain legible in the history
 * modal and in the active-chat header. Falls back to the session's
 * ``description`` if both are unavailable, mirroring ``getLastMessage``'s
 * prior-content fallback. Returns an empty string when the session has no
 * usable text — the caller chooses its own final fallback (e.g. ``Untitled
 * chat`` in the modal vs. ``New chat`` in the header).
 */
export function getSessionDisplayTitle(session: AIChatSession | null | undefined): string {
  if (!session) return ''
  const title = session.title?.trim()
  if (title) return title
  const preview = getFirstMessagePreview(session)
  if (preview) return preview
  return session.description?.trim() ?? ''
}

export function getFirstMessagePreview(session: AIChatSession): string {
  if (!session.messages?.length) return ''
  const raw = extractMessageText(session.messages[0])
  const text = raw.replace(/\s+/g, ' ').trim()
  if (!text) return ''
  // Slice by Unicode code points (Array.from) rather than UTF-16 code units so a
  // boundary that lands on a surrogate pair (emoji, CJK extension) doesn't render
  // as a replacement glyph in some browsers.
  const codePoints = Array.from(text)
  if (codePoints.length <= MAX_PREVIEW_LENGTH) return text
  return `${codePoints.slice(0, MAX_PREVIEW_LENGTH).join('').trimEnd()}…`
}
